import { call, query } from './client.ts'
import type { AgentCommand } from './admin.ts'
import type {
  ActivateProductKeyRequest,
  AllowIpRequest,
  AuthTokenResponse,
  BlockIpRequest,
  ChangePasswordRequest,
  CompanyAllowIpRequest,
  CompanyBlockIpRequest,
  CompanyIsolationPolicy,
  CompanyQuery,
  CompanyResponse,
  CreateCompanyRequest,
  CreateMachineRequest,
  CreateProductKeyRequest,
  CreateUserRequest,
  CurrentUserResponse,
  FleetIpCommandResponse,
  IsolateHostRequest,
  LoginRequest,
  MachineCommandQuery,
  MachineIpLists,
  MachineIsolationPolicy,
  MachineQuery,
  MachineResponse,
  ArtifactDetail,
  ArtifactQuery,
  ArtifactSummary,
  PagedResponse,
  ProductKeyQuery,
  ProductKeyResponse,
  ReleaseHostRequest,
  RevokeAllowIpRequest,
  SessionResponse,
  SetCompanyIsolationPolicyRequest,
  SetIsolationPolicyRequest,
  UnblockIpRequest,
  UninstallAgentRequest,
  UpdateCompanyRequest,
  UpdateMachineDisplayNameRequest,
  UpdateMachineRequest,
  UpdateProductKeyRequest,
  UpdateUserRequest,
  UserQuery,
  UserResponse,
  WebChallenge,
  WebChallengeQuery,
  WebDefenseExceptions,
} from './types.ts'

/**
 * Uma função por rota do manual, com o tipo do corpo em cada ponta.
 *
 * Só o transporte mora aqui: nada de estado, nada de cache, nada de tradução de
 * erro. Quem chama trata o `AthenaError`, porque a reação a um 409 depende da
 * tela — remover empresa com licença viva quer um caminho, revogar licença quer
 * outro.
 */

// ── auth ────────────────────────────────────────────────────────────────────

export const auth = {
  login: (body: LoginRequest) =>
    call<AuthTokenResponse>('/auth/login', { method: 'POST', body }),

  /** Anônimo de propósito: um access token vencido não pode impedir alguém de sair. */
  logout: () => call<void>('/auth/logout', { method: 'POST' }),

  logoutAll: () => call<void>('/auth/logout-all', { method: 'POST' }),

  me: (signal?: AbortSignal) => call<CurrentUserResponse>('/auth/me', { signal }),

  changePassword: (body: ChangePasswordRequest) =>
    call<void>('/auth/change-password', { method: 'POST', body }),

  sessions: (signal?: AbortSignal) => call<SessionResponse[]>('/auth/sessions', { signal }),

  revokeSession: (id: string) => call<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),
}

// ── empresas ────────────────────────────────────────────────────────────────

export const companies = {
  list: (params: CompanyQuery, signal?: AbortSignal) =>
    call<PagedResponse<CompanyResponse>>(
      `/companies${query({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        includeInactive: params.includeInactive,
        includeDeleted: params.includeDeleted,
      })}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) => call<CompanyResponse>(`/companies/${id}`, { signal }),

  create: (body: CreateCompanyRequest) =>
    call<CompanyResponse>('/companies', { method: 'POST', body }),

  update: (id: string, body: UpdateCompanyRequest) =>
    call<CompanyResponse>(`/companies/${id}`, { method: 'PUT', body }),

  /** Remoção lógica: carimba `deletedAt`, a linha fica porque a telemetria referencia o id. */
  remove: (id: string) => call<void>(`/companies/${id}`, { method: 'DELETE' }),

  /**
   * Bloqueio de IP na frota: um comando POR MÁQUINA. `202` mesmo com falhas —
   * o resultado é `queued`/`failed` e a lista por máquina, não um erro.
   */
  blockIp: (id: string, body: CompanyBlockIpRequest) =>
    call<FleetIpCommandResponse>(`/companies/${id}/commands/block-ip`, { method: 'POST', body }),

  allowIp: (id: string, body: CompanyAllowIpRequest) =>
    call<FleetIpCommandResponse>(`/companies/${id}/commands/allow-ip`, { method: 'POST', body }),

  /** Padrão de regime de isolamento para as máquinas NOVAS da empresa. `mode: null` = sem padrão. */
  isolationPolicy: (id: string, signal?: AbortSignal) =>
    call<CompanyIsolationPolicy>(`/companies/${id}/isolation-policy`, { signal }),

  /**
   * Só `platform_admin`. `200` com o objeto novo. Vale só para agentes que se
   * registrarem DEPOIS: as máquinas existentes não mudam.
   */
  setIsolationPolicy: (id: string, body: SetCompanyIsolationPolicyRequest) =>
    call<CompanyIsolationPolicy>(`/companies/${id}/isolation-policy`, { method: 'PUT', body }),
}

// ── máquinas ────────────────────────────────────────────────────────────────

export const machines = {
  list: (params: MachineQuery, signal?: AbortSignal) =>
    call<PagedResponse<MachineResponse>>(
      `/machines${query({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        includeDeleted: params.includeDeleted,
        isolated: params.isolated,
        companyId: params.companyId,
      })}`,
      { signal },
    ),

  /** O detalhe traz `isolation.last_command`; a listagem não. */
  get: (id: string, signal?: AbortSignal) => call<MachineResponse>(`/machines/${id}`, { signal }),

  create: (body: CreateMachineRequest) =>
    call<MachineResponse>('/machines', { method: 'POST', body }),

  update: (id: string, body: UpdateMachineRequest) =>
    call<MachineResponse>(`/machines/${id}`, { method: 'PUT', body }),

  /** Só o rótulo: renomear não transfere autorização nem muda o endpoint_id. Vazio limpa o apelido. */
  setDisplayName: (id: string, body: UpdateMachineDisplayNameRequest) =>
    call<MachineResponse>(`/machines/${id}/display-name`, { method: 'PATCH', body }),

  remove: (id: string) => call<void>(`/machines/${id}`, { method: 'DELETE' }),

  /**
   * Pede o desbloqueio de rede (`restore_host`). Só `platform_admin`. `202` com o
   * comando em `pending`: ACEITO não é FEITO — acompanhe por `commands.get`.
   */
  releaseHost: (id: string, body: ReleaseHostRequest) =>
    call<AgentCommand>(`/machines/${id}/commands/release-host`, { method: 'POST', body }),

  /**
   * Pede o isolamento de rede (`isolate_host`). Staff; vale em qualquer regime.
   * `202` com o comando em `pending`: só `executed` quer dizer isolada. Divide a
   * chave com o `releaseHost` — um aberto recusa o outro (409).
   */
  isolateHost: (id: string, body: IsolateHostRequest) =>
    call<AgentCommand>(`/machines/${id}/commands/isolate-host`, { method: 'POST', body }),

  /** Regime de isolamento: em vigor, pedido, confirmado e por que divergem. Staff. */
  isolationPolicy: (id: string, signal?: AbortSignal) =>
    call<MachineIsolationPolicy>(`/machines/${id}/isolation-policy`, { signal }),

  /**
   * Troca o regime (`set_isolation_policy`). Só `platform_admin`. `202` com o
   * comando: só `executed`/`skipped` confirmam. Um pedido ainda na fila é
   * substituído; um já entregue dá `409 agent_command_already_open`.
   */
  setIsolationPolicy: (id: string, body: SetIsolationPolicyRequest) =>
    call<AgentCommand>(`/machines/${id}/isolation-policy`, { method: 'PUT', body }),

  /**
   * Reemite a desinstalação do agente (`uninstall_agent`). Só `platform_admin`;
   * vale para máquina já removida. `202` com o comando em `pending`: só
   * `executed` quer dizer que a desinstalação começou. Os fluxos de revogar e
   * remover NÃO chamam isto — o Athena emite sozinho.
   */
  uninstall: (id: string, body: UninstallAgentRequest) =>
    call<AgentCommand>(`/machines/${id}/commands/uninstall`, { method: 'POST', body }),

  /**
   * Firewall de IP do host. Só `platform_admin` (qualquer empresa); respondem `202` com o comando em `pending` — o mesmo
   * fluxo do `releaseHost`: acompanhe por `commands.get`.
   */
  blockIp: (id: string, body: BlockIpRequest) =>
    call<AgentCommand>(`/machines/${id}/commands/block-ip`, { method: 'POST', body }),

  unblockIp: (id: string, body: UnblockIpRequest) =>
    call<AgentCommand>(`/machines/${id}/commands/unblock-ip`, { method: 'POST', body }),

  allowIp: (id: string, body: AllowIpRequest) =>
    call<AgentCommand>(`/machines/${id}/commands/allow-ip`, { method: 'POST', body }),

  revokeAllowIp: (id: string, body: RevokeAllowIpRequest) =>
    call<AgentCommand>(`/machines/${id}/commands/revoke-allow-ip`, { method: 'POST', body }),

  /** Bloqueados / allowlist / aprendidos, como o ATHENA os viu. Pendentes não aparecem. */
  ips: (id: string, signal?: AbortSignal) =>
    call<MachineIpLists>(`/machines/${id}/ips`, { signal }),

  /** Envelope camelCase do /api/v1; os itens são o comando snake_case. */
  commands: (id: string, params: MachineCommandQuery, signal?: AbortSignal) =>
    call<PagedResponse<AgentCommand>>(
      `/machines/${id}/commands${query({
        page: params.page,
        pageSize: params.pageSize,
        kind: params.kind,
        openOnly: params.openOnly,
      })}`,
      { signal },
    ),

  /** Artefatos coletados, do mais recente ao mais antigo. Só `platform_admin`. */
  artifacts: (id: string, params: ArtifactQuery, signal?: AbortSignal) =>
    call<PagedResponse<ArtifactSummary>>(
      `/machines/${id}/artifacts${query({
        artifact: params.artifact,
        page: params.page,
        pageSize: params.pageSize,
      })}`,
      { signal },
    ),

  /** Um challenge com a `evidence` preenchida. `404 web_challenge_not_found` se o Athena não o recebeu desta máquina. */
  webChallenge: (id: string, challengeId: string, signal?: AbortSignal) =>
    call<WebChallenge>(`/machines/${id}/web-defense/challenges/${encodeURIComponent(challengeId)}`, { signal }),

  /** A última listagem de exceções. `404 web_exceptions_not_listed` quando nunca foi listada. */
  webExceptions: (id: string, signal?: AbortSignal) =>
    call<WebDefenseExceptions>(`/machines/${id}/web-defense/exceptions`, { signal }),
}

// ── Web Defense ─────────────────────────────────────────────────────────────

export const webDefense = {
  /** Fila de challenges, mais recentes primeiro. Sem `state`, só os pendentes. */
  challenges: (params: WebChallengeQuery, signal?: AbortSignal) =>
    call<PagedResponse<WebChallenge>>(
      `/web-defense/challenges${query({
        state: params.state,
        companyId: params.companyId,
        endpointId: params.endpointId,
        ip: params.ip,
        page: params.page,
        pageSize: params.pageSize,
      })}`,
      { signal },
    ),
}

// ── artefatos ───────────────────────────────────────────────────────────────

export const artifacts = {
  get: (id: string, signal?: AbortSignal) =>
    call<ArtifactDetail>(`/artifacts/${id}`, { signal }),
}

// ── comandos ────────────────────────────────────────────────────────────────

export const commands = {
  get: (id: string, signal?: AbortSignal) => call<AgentCommand>(`/commands/${id}`, { signal }),
}

// ── licenças ────────────────────────────────────────────────────────────────

export const productKeys = {
  list: (params: ProductKeyQuery, signal?: AbortSignal) =>
    call<PagedResponse<ProductKeyResponse>>(
      `/product-keys${query({
        page: params.page,
        pageSize: params.pageSize,
        companyId: params.companyId,
        machineId: params.machineId,
        status: params.status,
        includeDeleted: params.includeDeleted,
      })}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) =>
    call<ProductKeyResponse>(`/product-keys/${id}`, { signal }),

  /** Tolera caixa e hífens fora do padrão — é a busca do suporte, que cola de e-mail. */
  lookup: (value: string, signal?: AbortSignal) =>
    call<ProductKeyResponse>(`/product-keys/lookup${query({ value })}`, { signal }),

  create: (body: CreateProductKeyRequest) =>
    call<ProductKeyResponse>('/product-keys', { method: 'POST', body }),

  /**
   * PUT com semântica de substituição total. `machineId` é obrigatório no tipo
   * justamente porque omiti-lo DESVINCULA a máquina — é a pegadinha da API.
   * Prefira `activate`/`suspend`/`revoke` quando a intenção for só o status.
   */
  update: (id: string, body: UpdateProductKeyRequest) =>
    call<ProductKeyResponse>(`/product-keys/${id}`, { method: 'PUT', body }),

  activate: (id: string, body: ActivateProductKeyRequest = {}) =>
    call<ProductKeyResponse>(`/product-keys/${id}/activate`, { method: 'POST', body }),

  suspend: (id: string) =>
    call<ProductKeyResponse>(`/product-keys/${id}/suspend`, { method: 'POST' }),

  /** Terminal. Não existe caminho de volta: religar exige emitir uma chave nova. */
  revoke: (id: string) =>
    call<ProductKeyResponse>(`/product-keys/${id}/revoke`, { method: 'POST' }),

  remove: (id: string) => call<void>(`/product-keys/${id}`, { method: 'DELETE' }),
}

// ── agentes ─────────────────────────────────────────────────────────────────

/**
 * Só o corte de acesso: registrar e listar agente não passa por esta API. O
 * registro exige prova de posse da chave privada, e um endpoint REST que apenas
 * gravasse a chave pública seria caminho para agente falso.
 */
export const agents = {
  revoke: (agentId: string) => call<void>(`/agents/${agentId}/revoke`, { method: 'POST' }),
  suspend: (agentId: string) => call<void>(`/agents/${agentId}/suspend`, { method: 'POST' }),
  activate: (agentId: string) => call<void>(`/agents/${agentId}/activate`, { method: 'POST' }),
}

/** SHA-256 da chave pública: 64 hex minúsculos. Não é GUID, não valide como tal. */
export const AGENT_ID_PATTERN = /^[0-9a-f]{64}$/

export function isAgentId(value: string): boolean {
  return AGENT_ID_PATTERN.test(value)
}

// ── usuários ────────────────────────────────────────────────────────────────

export const users = {
  list: (params: UserQuery, signal?: AbortSignal) =>
    call<PagedResponse<UserResponse>>(
      `/users${query({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        role: params.role,
        includeInactive: params.includeInactive,
        includeDeleted: params.includeDeleted,
        companyId: params.companyId,
        scope: params.scope,
      })}`,
      { signal },
    ),

  get: (id: string, signal?: AbortSignal) => call<UserResponse>(`/users/${id}`, { signal }),

  create: (body: CreateUserRequest) => call<UserResponse>('/users', { method: 'POST', body }),

  update: (id: string, body: UpdateUserRequest) =>
    call<UserResponse>(`/users/${id}`, { method: 'PUT', body }),

  /** Redefinição administrativa. */
  resetPassword: (id: string) => call<void>(`/users/${id}/password`, { method: 'PUT' }),

  remove: (id: string) => call<void>(`/users/${id}`, { method: 'DELETE' }),
}
